import { ApolloError } from '@apollo/client'
import { datadogRum } from '@datadog/browser-rum'
import { Referrers } from '../../consts/defaultConfigValues/defaultConstants'
import usePrefillCartMutation from '../../graphQL/mutations/prefillCart'
import { prefillCartAllOrNothingMutationQuery } from '../../graphQL/mutations/prefillCart/mutation'
import { IPrefillCartMutationResponse } from '../../graphQL/mutations/prefillCart/types'
import { ICartProduct, ICartProductInput } from '../../interfaces'
import { setSynchronizingCart } from '../../redux/slices/catalog'
import { addAlert } from '../../redux/slices/config'
import { RootState } from '../../redux/store'
import { onCartResponse } from '../../utils/onCartResponse'
import { noteCartWriteSettled } from '../../utils/settledCartWrites'
import useAppDispatch from '../useAppDispatch'
import useAppSelector from '../useAppSelector'
import useAppStore from '../useAppStore'
import { generateDataDogContext } from '../../utils/dataDog'
import { fireGTMEvent } from '../../utils/fireGTMEvent'
import { parseAnalyticsItems } from '../../utils/getAnalyticsItems'
import { ItemListNames } from '../../interfaces/tag_manager'
import {
  buildAllOrNothingMessage,
  buildAllOrNothingUnknownOutcomeMessage,
  readAllOrNothingRejection,
} from '../../utils/promotions/addAllOrNothingRejection'

/**
 * Product names the page already holds, by sku: the open product, the loaded
 * catalogue and the cart. A refusal can reject a line the button sent with no
 * name, or a gift storefront added on its own.
 */
const knownProductNames = (state: RootState): Map<string, string> => {
  const names = new Map<string, string>()
  const add = (sku?: string | null, name?: string | null) => {
    if (sku && name && !names.has(sku)) names.set(sku, name)
  }
  const { selectedProduct, categories } = state.catalogSlice
  add(selectedProduct?.sku, selectedProduct?.name)
  categories?.forEach((category) =>
    category.productsPrices?.forEach((product) =>
      add(product.sku, product.name),
    ),
  )
  state.cartSlice.items?.forEach((item) => add(item.sku, item.name))
  return names
}

export type IAddPrefillCartItem = Pick<
  ICartProduct,
  'sku' | 'quantity' | 'package' | 'price' | 'discounts' | 'name'
>

interface AddPrefillCartArgs {
  items: IAddPrefillCartItem[]
  storefrontName: string
  sessionUid: string
  onCompleted?: (data: IPrefillCartMutationResponse) => void
  replaceCart?: boolean
  referrer?: Referrers
  /**
   * Send the `PrefillCartAllOrNothing` document with `allOrNothing: true`.
   * Only the promotion add-all button passes it; any other value sends the
   * plain `PrefillCart` request every caller has always sent.
   */
  allOrNothing?: boolean
}

export const useAddPrefillCart = () => {
  const dispatch = useAppDispatch()
  const store = useAppStore()
  const currency = useAppSelector(
    (state) => state.defaultSlice.config?.options?.currencyPrefix || '',
  )

  /**
   * Handles onComplete callback from prefill cart mutation.
   *
   * Reads the store when the response lands, not the render-time snapshot.
   * Nothing raises a loader or blocks navigation while the prefill is in
   * flight, so the shopper may have left the page by the time Apollo calls
   * this.
   *
   * A snapshot from a page that has since unmounted still holds that page's
   * lists, and `onCartResponse` would write them back over the next page's:
   * among them the catalogue list, the open product and the category-nav
   * list. Which way the category-nav list goes wrong depends on the old page.
   * One that never loads it, such as the cart, holds it as `null`, so the
   * next page's list is skipped rather than updated; a category-nav page has
   * it refilled by `useGetProductsNav`, so its own list is written back over
   * the next page's. See `utils/onCartResponse.ts` for the full set, and
   * `useDeleteProductFromCart` (`useDeleteItemFromCart.ts`) for the same
   * hazard.
   */
  const onCompletePrefillCartMutation = (
    data: IPrefillCartMutationResponse,
  ) => {
    // Counted before the cart is written, success or not, so a refresh
    // whose cart read is out reads it again (COM-1512)
    noteCartWriteSettled()
    if (data?.prefillCart) {
      onCartResponse({
        data: data.prefillCart,
        state: store.getState(),
        dispatch,
      })
    }
    dispatch(setSynchronizingCart(false))
  }

  /**
   * Handles onError callback from prefill cart mutation
   */
  const onErrorPrefillCartMutation = (error: ApolloError) => {
    noteCartWriteSettled()
    dispatch(setSynchronizingCart(false))
    const context = generateDataDogContext({
      title: 'Prefill cart mutation error',
      extraInfo: { function: 'usePrefillCartMutation' },
    })
    datadogRum.startView(context.viewName)
    datadogRum.addError(error, context)
  }

  /**
   * Get prefillCart mutation hook
   */
  const [sendPrefillCart] = usePrefillCartMutation({
    onCompleted: onCompletePrefillCartMutation,
    onError: onErrorPrefillCartMutation,
  })

  /**
   * The all-or-nothing request's outcome when it fails, handled per call so
   * overlapping taps each name their own lines.
   *
   * A refusal is an expected outcome once a storefront opts in, so it gets the
   * shopper's message and no error report, except for the reasons the message
   * cannot word and for a bundle needing a selection, which is counted. Any
   * other failure (a network error, a 5xx, the max-quantity checkout rule, an
   * old storefront, an add-on tenant) proves nothing about the cart: an
   * accepted write can lose its response. So it says the outcome is unknown,
   * never that nothing was added, and reports under its own title. Nothing here calls
   * `datadogRum.startView`: this only ever runs from the product page's add-all
   * button, whose manually tracked view a restart would end.
   */
  const reportAllOrNothingFailure = (
    error: ApolloError,
    requested: IAddPrefillCartItem[],
  ) => {
    const state = store.getState()
    const { config } = state.defaultSlice
    const rejection = readAllOrNothingRejection(error)
    if (!rejection) {
      const message = buildAllOrNothingUnknownOutcomeMessage(config)
      if (message) dispatch(addAlert({ message }))
      datadogRum.addError(
        error,
        generateDataDogContext({
          title: 'Prefill cart all-or-nothing error',
          extraInfo: { function: 'useAddPrefillCart' },
        }),
      )
      return
    }
    const { message, unexpected, selectionRequired } = buildAllOrNothingMessage(
      {
        rejection,
        requested,
        config,
        knownNames: knownProductNames(state),
      },
    )
    if (message) dispatch(addAlert({ message }))
    if (unexpected.length || rejection.unreadable) {
      datadogRum.addError(
        new Error('promotion add-all rejected with an unexpected reason'),
        generateDataDogContext({
          title: 'promotion add-all rejected with an unexpected reason',
          extraInfo: {
            function: 'useAddPrefillCart',
            reasons: JSON.stringify(unexpected.map((line) => line.reason)),
            skus: JSON.stringify(unexpected.map((line) => line.sku)),
            unreadable: rejection.unreadable,
          },
        }),
      )
    }
    if (selectionRequired.length) {
      datadogRum.addError(
        new Error('promotion add-all rejected: bundle selection required'),
        generateDataDogContext({
          title: 'promotion add-all rejected: bundle selection required',
          extraInfo: {
            function: 'useAddPrefillCart',
            reasons: JSON.stringify(
              selectionRequired.map((line) => line.reason),
            ),
            skus: JSON.stringify(selectionRequired.map((line) => line.sku)),
          },
        }),
      )
    }
  }

  /**
   * Adds prefill cart items to cart.
   *
   * Returns the mutation payload on success and `null` on failure, because a
   * caller cannot learn that any other way. Apollo's `useMutation` only
   * rejects when no `onError` is registered; with one — as above — it
   * resolves with `{ data: undefined, errors }` instead, so a `try`/`catch`
   * around this call never sees a failed mutation.
   */
  const addPrefillCart = async ({
    items,
    storefrontName,
    sessionUid,
    onCompleted,
    replaceCart,
    referrer = Referrers.PREFILL_CART,
    allOrNothing,
  }: AddPrefillCartArgs) => {
    dispatch(setSynchronizingCart(true))
    const filteredItems = items.filter((item) => item.quantity > 0)

    const fireAddToCart = () =>
      fireGTMEvent('commerce-add_to_cart', {
        currency,
        value: filteredItems.reduce(
          (acc, { price, quantity }) => acc + price * quantity,
          0,
        ),
        items: filteredItems.map((item, index) =>
          parseAnalyticsItems(item, index),
        ),
        // Reorder from History / Detail attributes the add to the reorder
        // surface for GA4. Other prefill callers leave `item_list_name` unset.
        ...(referrer === Referrers.ORDER_HISTORY_REORDER
          ? { item_list_name: ItemListNames.REORDER }
          : {}),
      })
    // A refused all-or-nothing request adds nothing, so it is counted only
    // once storefront accepts it. Every other request keeps firing first.
    if (allOrNothing !== true) fireAddToCart()
    const itemsToAdd = filteredItems.map<ICartProductInput>((item) => ({
      sku: item.sku,
      quantity: item.quantity,
      selectedPackage: item.package?.name,
    }))

    const variables = {
      storefrontName,
      sessionUid,
      items: itemsToAdd,
      replaceCart,
      referrer,
    }
    /*
     * The all-or-nothing call swaps the document and the error handler for
     * this call only; Apollo merges execute options over the hook's. Its
     * `onError` replaces the hook's, which would restart the Datadog view, and
     * keeps the error in this call's scope so overlapping taps cannot answer
     * for each other.
     */
    let allOrNothingFailure: ApolloError | undefined
    const response =
      allOrNothing === true
        ? await sendPrefillCart({
            mutation: prefillCartAllOrNothingMutationQuery,
            variables: { ...variables, allOrNothing: true },
            onError: (error) => {
              // Replaces the hook's `onError`, so it counts the settle too
              noteCartWriteSettled()
              dispatch(setSynchronizingCart(false))
              allOrNothingFailure = error
            },
          })
        : await sendPrefillCart({ variables })
    if (allOrNothingFailure)
      reportAllOrNothingFailure(allOrNothingFailure, items)
    if (response?.data) {
      if (allOrNothing === true) fireAddToCart()
      onCompleted?.(response.data)
      return response.data
    }
    return null
  }

  return { addPrefillCart }
}
